/**
 * Bulk answers in the Inbox (ADR 0072): the bottom bar for a selection, named by its count, with
 * Allow once, Allow for session, Deny and Dismiss. One batch call answers the requests
 * (POST /api/approvals/batch), each checked and audited by the hub as a single answer, and what did
 * not go through is listed per request. Allowing high risk in bulk asks once, by count. Allow for
 * session shows each pattern, editable. Dismiss takes only asked questions: nothing blocked is
 * ever dismissed in bulk (ADR 0056).
 */
import { Ban, BellOff, Check, CheckCheck, ListChecks } from '@orgus/ui/icons';
import { useState } from 'react';
import { askIdOf } from '@orgus/protocol';
import type { ApprovalBatchResult, ApprovalRequest } from '@orgus/protocol';
import { BulkBar, Button, ConfirmDialog, Dialog, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Input, Textarea, plural, toUiError, toast } from '@orgus/ui';
import type { BulkAction as BarAction } from '@orgus/ui';
import { dismissAsks } from '../asks/api.ts';
import { ACTION_LABEL, bulkPlan, patternHint, requestsWord } from './decision.ts';
import type { BulkAction, BulkPlan } from './decision.ts';
import { decideBatch } from './live.ts';
import { shortTitle, titleParts } from './inboxModel.ts';
import type { InboxItem } from './inboxModel.ts';

/** What a bulk answer did, for the banner over the list: the count that went through, and each one that did not, with why. */
export interface BulkOutcome {
  line: string;
  failures: { id: string; title: string; why: string }[];
}

/** The banner text of a batch result: "6 requests allowed. 1 was not:" and each refusal. */
export function outcomeOf(res: ApprovalBatchResult, verb: string, titles: ReadonlyMap<string, string>): BulkOutcome {
  const failures = res.results
    .filter((r) => !r.ok)
    .map((r) => ({ id: r.id, title: titles.get(r.id) ?? r.id, why: [r.error?.why ?? r.error?.what, r.error?.next].filter(Boolean).join(' ') }));
  const done = res.decided ? `${requestsWord(res.decided)} ${verb}.` : `No request was ${verb}.`;
  return { line: failures.length ? `${done} ${plural(failures.length, 'was', 'were')} not:` : done, failures };
}

/** A request's verb and its whole target ("Run git push --force-with-lease origin fix/login-race"). */
const fullTitle = (a: Pick<ApprovalRequest, 'title' | 'input'>): string => {
  const p = titleParts(a);
  return p.full ? `${p.verb} ${p.full}` : p.verb;
};

const VERB: Record<BulkAction, string> = { 'allow-once': 'allowed', 'allow-session': 'allowed for their sessions', deny: 'denied' };

/** One editable pattern for the requests that share a session, a tool and a suggested pattern. */
interface PatternRow {
  key: string;
  tool: string;
  session: string;
  ids: string[];
  pattern: string;
}

function patternRows(take: readonly ApprovalRequest[], sessionName: (id: string) => string): PatternRow[] {
  const rows = new Map<string, PatternRow>();
  for (const a of take) {
    const key = `${a.sessionId}\u0000${a.tool ?? ''}\u0000${a.suggestedPattern ?? ''}`;
    const row = rows.get(key) ?? { key, tool: a.tool ?? 'Tool', session: sessionName(a.sessionId), ids: [], pattern: a.suggestedPattern ?? '' };
    row.ids.push(a.id);
    rows.set(key, row);
  }
  return [...rows.values()];
}

export interface InboxBulkProps {
  /**
   * 'bar' (default): the bottom bar for a selection. 'menu': one "Answer all" button with the same
   * answers in a menu, for a group header (the phone's session groups).
   */
  variant?: 'bar' | 'menu';
  selected: readonly InboxItem[];
  total: number;
  onClear: () => void;
  onOutcome: (o: BulkOutcome | null) => void;
  sessionName: (id: string) => string;
}

export function InboxBulk({ variant = 'bar', selected, total, onClear, onOutcome, sessionName }: InboxBulkProps) {
  const requests = selected.flatMap((i) => (i.kind === 'request' || i.kind === 'ask' ? [i.approval] : []));
  const asked = selected.flatMap((i) => (i.kind === 'asked' ? [i] : []));
  const plans: Record<BulkAction, BulkPlan<ApprovalRequest>> = {
    'allow-once': bulkPlan(requests, 'allow-once'),
    'allow-session': bulkPlan(requests, 'allow-session'),
    deny: bulkPlan(requests, 'deny'),
  };
  const [open, setOpen] = useState<BulkAction | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [patterns, setPatterns] = useState<PatternRow[]>([]);

  const titles = new Map(requests.map((a) => [a.id, shortTitle(a, 40)]));

  /** Send one batch. Resolves when it went through (in part or whole); throws when the call itself failed. */
  const send = async (action: BulkAction, extra: { note?: string; patterns?: Record<string, string>; confirmHighRisk?: boolean } = {}) => {
    const take = plans[action].take;
    const res = await decideBatch({
      ids: take.map((a) => a.id),
      answer: action === 'deny' ? 'deny' : 'allow',
      scope: action === 'allow-session' ? 'session' : 'once',
      ...(extra.note?.trim() ? { note: extra.note.trim() } : {}),
      ...(extra.patterns ? { patterns: extra.patterns } : {}),
      ...(extra.confirmHighRisk ? { confirmHighRisk: true } : {}),
    });
    const o = outcomeOf(res, VERB[action], titles);
    onOutcome(o.failures.length ? o : null);
    if (res.decided) toast(`${requestsWord(res.decided)} ${VERB[action]}`, { tone: action === 'deny' ? 'neutral' : 'success' });
  };

  const runNow = async (action: BulkAction) => {
    setBusy(true);
    try {
      await send(action);
    } catch (e) {
      const err = toUiError(e);
      onOutcome({ line: [err.what, err.why, err.next].filter(Boolean).join(' '), failures: [] });
    } finally {
      setBusy(false);
    }
  };

  const openDialog = (action: BulkAction) => {
    setError(null);
    setNote('');
    if (action === 'allow-session') setPatterns(patternRows(plans['allow-session'].take, sessionName));
    setOpen(action);
  };

  const inDialog = async (action: BulkAction, extra: Parameters<typeof send>[1]) => {
    setBusy(true);
    setError(null);
    try {
      await send(action, extra);
      setOpen(null);
    } catch (e) {
      const err = toUiError(e);
      setError([err.what, err.why, err.next].filter(Boolean).join(' '));
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      const n = await dismissAsks(asked.map((i) => ({ askId: askIdOf(i.session), sessionId: i.session.id, reason: '' })));
      toast(`${plural(n, 'question')} dismissed`);
      onOutcome(null);
    } catch (e) {
      const err = toUiError(e);
      onOutcome({ line: [err.what, err.why, err.next].filter(Boolean).join(' '), failures: [] });
    } finally {
      setBusy(false);
    }
  };

  /** What an action's hint says: how many it takes, and why it leaves the others. */
  const hintOf = (action: BulkAction): string => {
    const p = plans[action];
    if (!p.take.length) return p.skip[0]?.why ?? 'Nothing selected can take this answer.';
    const left = selected.length - p.take.length;
    const parts = [`${action === 'deny' ? 'Denies' : 'Allows'} ${requestsWord(p.take.length)}.`];
    if (left > 0) parts.push(`Leaves ${plural(left, 'item')}: ${p.skip[0]?.why ?? 'it takes another answer.'}`);
    if (action !== 'deny' && p.high.length) parts.push(`Asks you to confirm ${plural(p.high.length, 'high-risk request')}.`);
    return parts.join(' ');
  };

  const actions: BarAction[] = [
    {
      id: 'allow-once',
      label: ACTION_LABEL['allow-once'],
      icon: <Check />,
      disabled: busy || !plans['allow-once'].take.length,
      hint: hintOf('allow-once'),
      onClick: () => (plans['allow-once'].high.length ? openDialog('allow-once') : void runNow('allow-once')),
    },
    {
      id: 'allow-session',
      label: `${ACTION_LABEL['allow-session']}…`,
      icon: <CheckCheck />,
      disabled: busy || !plans['allow-session'].take.length,
      hint: hintOf('allow-session'),
      onClick: () => openDialog('allow-session'),
    },
    {
      id: 'deny',
      label: `${ACTION_LABEL.deny}…`,
      icon: <Ban />,
      disabled: busy || !plans.deny.take.length,
      hint: hintOf('deny'),
      onClick: () => openDialog('deny'),
    },
    ...(asked.length
      ? [
          {
            id: 'dismiss',
            label: 'Dismiss',
            icon: <BellOff />,
            disabled: busy,
            hint: `Dismisses ${plural(asked.length, 'asked question')}. Nothing blocked is dismissed in bulk.`,
            onClick: () => void dismiss(),
          },
        ]
      : []),
  ];

  const once = plans['allow-once'];
  const session = plans['allow-session'];
  const deny = plans.deny;

  return (
    <>
      {/* Select all lives on each group's header box; the bar keeps to the answers so it fits beside the docked panel. */}
      {variant === 'bar' ? (
        <BulkBar inline count={selected.length} noun="item" total={total} onClear={onClear} actions={actions} />
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="secondary" icon={<ListChecks />} loading={busy}>
              Answer all {selected.length}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {actions.map((x) => (
              <DropdownMenuItem key={x.id} icon={x.icon} disabled={x.disabled} onSelect={x.onClick}>
                {x.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <ConfirmDialog
        open={open === 'allow-once'}
        onOpenChange={(o) => !o && setOpen(null)}
        title={`Allow ${requestsWord(once.take.length)}?`}
        description={`${plural(once.high.length, 'is', 'are')} high risk and will run as soon as you confirm.`}
        confirmLabel={`Allow ${requestsWord(once.take.length)}`}
        cancelLabel="Keep waiting"
        onConfirm={() => send('allow-once', { confirmHighRisk: true })}
      >
        {/* Each high-risk command whole, wrapped (never cut): it is what you are about to allow. */}
        <ul className="flex w-full min-w-0 flex-col gap-1 rounded-control bg-sunken px-3 py-2 font-mono text-caption text-fg-muted">
          {once.high.slice(0, 3).map((a) => (
            <li key={a.id} className="min-w-0 break-all">
              {fullTitle(a)}
            </li>
          ))}
          {once.high.length > 3 && <li className="text-fg-subtle">+{once.high.length - 3}</li>}
        </ul>
        {/* The count on the action is what runs; the rest of the selection is named as a caption. */}
        {selected.length > once.take.length && <p className="text-caption text-fg-subtle">{plural(selected.length - once.take.length, 'other item stays', 'other items stay')} waiting</p>}
      </ConfirmDialog>

      <Dialog
        open={open === 'allow-session'}
        onOpenChange={(o) => !busy && !o && setOpen(null)}
        title={`Allow ${requestsWord(session.take.length)} for their sessions`}
        description={session.high.length ? `Includes ${plural(session.high.length, 'high-risk request')}; allowing confirms it.` : 'Each session runs a matching call without asking again.'}
        dismissible={!busy}
        footer={
          <>
            <Button variant="ghost" disabled={busy} onClick={() => setOpen(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              icon={<CheckCheck />}
              loading={busy}
              onClick={() =>
                void inDialog('allow-session', {
                  patterns: Object.fromEntries(patterns.flatMap((r) => r.ids.map((id) => [id, r.pattern.trim()]))),
                  confirmHighRisk: session.high.length > 0,
                })
              }
            >
              {ACTION_LABEL['allow-session']}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {patterns.map((r, n) => (
            <Input
              key={r.key}
              label={`${r.tool} in ${r.session}${r.ids.length > 1 ? `, ${plural(r.ids.length, 'request')}` : ''}`}
              value={r.pattern}
              spellCheck={false}
              className="font-mono"
              disabled={busy}
              hint={patternHint(r.pattern, r.tool)}
              onChange={(e) => setPatterns((rows) => rows.map((x, i) => (i === n ? { ...x, pattern: e.target.value } : x)))}
            />
          ))}
          {error && (
            <p role="alert" className="rounded-control bg-status-danger-soft px-3 py-2 text-ui text-fg">
              {error}
            </p>
          )}
        </div>
      </Dialog>

      <Dialog
        open={open === 'deny'}
        onOpenChange={(o) => !busy && !o && setOpen(null)}
        title={`Deny ${requestsWord(deny.take.length)}`}
        dismissible={!busy}
        footer={
          <>
            <Button variant="ghost" disabled={busy} onClick={() => setOpen(null)}>
              Cancel
            </Button>
            <Button variant="primary" icon={<Ban />} loading={busy} onClick={() => void inDialog('deny', { note })}>
              Deny requests
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Textarea label="Note for the agents" rows={3} value={note} placeholder="Optional" maxLength={1000} disabled={busy} onChange={(e) => setNote(e.target.value)} />
          {error && (
            <p role="alert" className="rounded-control bg-status-danger-soft px-3 py-2 text-ui text-fg">
              {error}
            </p>
          )}
        </div>
      </Dialog>
    </>
  );
}
